
import { createContext, useContext, useEffect, useState } from "react"
import { createClient } from "@/lib/supabase/client"
import type { User } from "@supabase/supabase-js"

interface AuthContextType {
  user: User | null
  loading: boolean
  signOut: () => Promise<void>
  updateProfile: (fullName: string) => Promise<{ error: Error | null }>
  updatePassword: (newPassword: string) => Promise<{ error: Error | null }>
  deleteAccount: () => Promise<{ error: Error | null }>
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  signOut: async () => {},
  updateProfile: async () => ({ error: null }),
  updatePassword: async () => ({ error: null }),
  deleteAccount: async () => ({ error: null }),
})

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const supabase = createClient()

  useEffect(() => {
    console.log('🔐 [AUTH] Initializing auth context', {
      timestamp: new Date().toISOString()
    })

    // Get initial session
    const getUser = async () => {
      console.log('🔐 [AUTH] Fetching initial user session...')
      try {
        const {
          data: { user },
          error
        } = await supabase.auth.getUser()

        if (error) {
          console.error('❌ [AUTH] Error fetching user:', {
            error,
            message: error.message,
            timestamp: new Date().toISOString()
          })

          // Handle invalid refresh token by clearing session
          if (error.message.includes('Refresh Token') || error.message.includes('Invalid')) {
            console.log('🧹 [AUTH] Clearing invalid session from localStorage')
            await supabase.auth.signOut({ scope: 'local' })
          }
        }

        console.log('🔐 [AUTH] Initial user fetch complete', {
          hasUser: !!user,
          userId: user?.id,
          email: user?.email,
          timestamp: new Date().toISOString()
        })

        setUser(user)
        setLoading(false)
      } catch (err) {
        console.error('❌ [AUTH] Exception during user fetch:', err)

        // Clear any corrupted session data
        try {
          await supabase.auth.signOut({ scope: 'local' })
        } catch (signOutError) {
          console.warn('⚠️ [AUTH] Failed to clear session:', signOutError)
        }

        setUser(null)
        setLoading(false)
      }
    }

    getUser()

    // Listen for auth changes
    console.log('🔐 [AUTH] Setting up auth state listener')
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('🔐 [AUTH] Auth state changed', {
        event,
        hasSession: !!session,
        hasUser: !!session?.user,
        userId: session?.user?.id,
        email: session?.user?.email,
        timestamp: new Date().toISOString()
      })

      setUser(session?.user ?? null)
      setLoading(false)
    })

    return () => {
      console.log('🔐 [AUTH] Cleaning up auth listener', {
        timestamp: new Date().toISOString()
      })
      subscription.unsubscribe()
    }
  }, []) // supabase is a singleton, no need to track it

  const signOut = async () => {
    console.log('🚪 [AUTH] Sign out initiated', {
      timestamp: new Date().toISOString(),
      hasUser: !!user,
      userId: user?.id,
      email: user?.email
    })

    try {
      const { error } = await supabase.auth.signOut()

      if (error) {
        console.error('❌ [AUTH] Sign out error:', {
          error,
          message: error.message,
          code: error.code,
          status: error.status,
          timestamp: new Date().toISOString()
        })
        throw error
      }

      console.log('✅ [AUTH] Sign out successful', {
        timestamp: new Date().toISOString()
      })

      setUser(null)
    } catch (err) {
      console.error('❌ [AUTH] Exception during sign out:', err)
      // Clear user state even if signOut fails to prevent stuck logged-in UI
      setUser(null)
      throw err
    }
  }

  const updateProfile = async (fullName: string) => {
    try {
      const { data, error } = await supabase.auth.updateUser({
        data: { full_name: fullName }
      })

      if (error) throw error
      if (data.user) setUser(data.user)
      return { error: null }
    } catch (error) {
      return { error: error as Error }
    }
  }

  const updatePassword = async (newPassword: string) => {
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      })

      if (error) throw error
      return { error: null }
    } catch (error) {
      return { error: error as Error }
    }
  }

  const deleteAccount = async () => {
    try {
      // Delete all user expenses first
      const { error: deleteExpensesError } = await supabase
        .from('user_expenses')
        .delete()
        .eq('user_id', user?.id)

      if (deleteExpensesError) throw deleteExpensesError

      // Delete all user incomes
      const { error: deleteIncomesError } = await supabase
        .from('user_incomes')
        .delete()
        .eq('user_id', user?.id)

      if (deleteIncomesError) throw deleteIncomesError

      // Sign out and clear local state
      await supabase.auth.signOut()
      setUser(null)

      return { error: null }
    } catch (error) {
      return { error: error as Error }
    }
  }

  return (
    <AuthContext.Provider
      value={{ user, loading, signOut, updateProfile, updatePassword, deleteAccount }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider")
  }
  return context
}
